import { useCallback, useEffect, useMemo, useState } from "react"
import { dataService } from "utils/dataService"
import { getLoggerInstance } from "utils/logger"
const logger = getLoggerInstance()

export const useAccessCode = ({
  validatedAccessCode,
  setValidatedAccessCode,
  unitId,
  ivrAccessCode,
  answeredCall,
  interactionId,
  accessCodeObject,
  loadingAccessCode,
  getPartnerAccessCode,
  refreshCallSheet,
}) => {
  const [validatingAccessCode, setValidatingAccessCode] = useState(false)
  const [validateAccessCodeStatus, setValidationStatus] = useState(null)
  const [validateAccessCodeResponse, setValidationResponse] = useState("")

  const validAccessCode = useMemo(
    () => accessCodeObject || unitId || ivrAccessCode || validatedAccessCode,
    [accessCodeObject, ivrAccessCode, unitId, validatedAccessCode]
  )

  const requiresAccessCode = useMemo(
    () => !loadingAccessCode && !accessCodeObject && !answeredCall?.interaction?.unitId,
    [accessCodeObject, answeredCall, loadingAccessCode]
  )

  const requestValidation = useCallback(
    async (code) => {
      try {
        await dataService.Interactions.AccessCode.validate({ interactionId, accessCode: code })
        logger.debug(`validated access code ${code}`)
        setValidatedAccessCode(true)
        setValidationResponse(`Successfully validated code ${code}`)
        setValidationStatus("success")
        return true
      } catch (err) {
        logger.warn(`validation failed for access code ${code}`, err)
        setValidatedAccessCode(false)
        setValidationResponse(err?.response?.data?.message || `Access code "${code}" not validated`)
        setValidationStatus("error")
        return false
      }
    },
    [interactionId, setValidatedAccessCode]
  )

  const loadCallSheetAfterValidation = useCallback(
    async (code) => {
      let callSheetData
      try {
        callSheetData = await refreshCallSheet()
      } catch (err) {
        logger.error(`call sheet refresh failed after validating code ${code}`, err)
        return
      }
      try {
        await getPartnerAccessCode({ accessCode: code, partnerId: callSheetData.partnerId })
      } catch (err) {
        logger.error(`partner access code lookup failed after validating code ${code}`, err)
      }
    },
    [getPartnerAccessCode, refreshCallSheet]
  )

  const validateAccessCode = useCallback(
    async (code) => {
      if (!interactionId) {
        setValidatedAccessCode(false)
        setValidationResponse("Still loading the call. Please try again in a moment.")
        setValidationStatus("error")
        return
      }
      const sanitizedCode = (code || "").replace(/\s+/g, "")
      if (!sanitizedCode) {
        return
      }
      setValidatingAccessCode(true)
      try {
        const isValid = await requestValidation(sanitizedCode)
        if (isValid) {
          await loadCallSheetAfterValidation(sanitizedCode)
        }
      } finally {
        setValidatingAccessCode(false)
      }
    },
    [interactionId, requestValidation, loadCallSheetAfterValidation, setValidatedAccessCode]
  )

  const resetValidation = useCallback(() => {
    setValidatedAccessCode(false)
    setValidationResponse("")
    setValidationStatus("")
  }, [setValidatedAccessCode])

  useEffect(() => {
    if (!answeredCall) {
      resetValidation()
    }
  }, [answeredCall, resetValidation])

  useEffect(() => {
    resetValidation()
  }, [interactionId, resetValidation])

  return {
    requiresAccessCode,
    validAccessCode,
    validateAccessCode,
    validatingAccessCode,
    validateAccessCodeStatus,
    validateAccessCodeResponse,
    canValidateAccessCode: Boolean(interactionId),
  }
}
